import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AuthUser,
  ChangeEmailRequest,
  LoginRequest,
  LoginResponse,
  SetPasswordRequest,
  UpdateProfileRequest,
} from '../models/auth.models';
import { UserRole } from '../../users/models/user.model';
import { TERMS_VERSION } from '../../terms-of-use/terms-version';
import { TRAINING_DISMISSED_KEY } from '../../home/training-card/training-card';
import { REFERRAL_DISMISSED_KEY } from '../../school-license/referral-card/referral-card';

interface JwtPayload {
  sub?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  exp?: number;
}

const AUTH_TOKEN_KEY = 'AUTH_TOKEN';
const AUTH_USER_KEY = 'AUTH_USER';

/**
 * Tokens expiring within this window are treated as already expired, so a request is not sent
 * only to expire in flight. Kept short: the API's dev profile issues very short-lived tokens,
 * and a long window would eat most of their life.
 */
const EXPIRY_LEEWAY_MS = 1_000;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  #currentUser = signal<AuthUser | null>(null);
  readonly currentUser = this.#currentUser.asReadonly();
  readonly isAuthenticated = computed(() => this.#currentUser() !== null);
  readonly isAdmin = computed(() => this.#currentUser()?.role === 'admin');
  /**
   * False until the user has accepted the current wording of the terms — on their first login, and
   * again whenever `TERMS_VERSION` moves. A stored session without the field counts as not
   * accepted, which is the safe reading.
   */
  readonly hasAcceptedCurrentTerms = computed(
    () => this.#currentUser()?.termsAcceptedVersion === TERMS_VERSION,
  );

  /** True when the session ended because its token expired, rather than by the user logging out. */
  #sessionExpired = signal(false);
  readonly sessionExpired = this.#sessionExpired.asReadonly();

  /** True when the session ended because the user confirmed a new login e-mail. */
  #emailChanged = signal(false);
  readonly emailChanged = this.#emailChanged.asReadonly();

  constructor() {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      this.clearSession();
      return;
    }

    if (this.isTokenExpired(token)) {
      // The token died while the app was closed — start signed out instead of letting the
      // guard through and failing every request afterwards.
      this.clearSession();
      this.#sessionExpired.set(true);
      return;
    }

    const userJson = localStorage.getItem(AUTH_USER_KEY);
    const user = userJson ? this.parseStoredUser(userJson) : this.parseJwt(token);
    if (user) {
      this.#currentUser.set(user);
    } else {
      this.clearSession();
    }
  }

  /**
   * Renames the account holder. The response is the same user the login returns, so the store is
   * refreshed straight from it — no re-read.
   */
  async updateProfile(req: UpdateProfileRequest): Promise<AuthUser> {
    const user = await firstValueFrom(
      this.http.put<AuthUser>(`${environment.apiUrl}/auth/me`, req),
    );
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    this.#currentUser.set(user);
    return user;
  }

  /**
   * Records the user's consent to the current terms. The response is the same user the login
   * returns, so the store is refreshed straight from it and `hasAcceptedCurrentTerms` flips.
   */
  async acceptTerms(): Promise<void> {
    const user = await firstValueFrom(
      this.http.post<AuthUser>(`${environment.apiUrl}/auth/accept-terms`, {
        version: TERMS_VERSION,
      }),
    );
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    this.#currentUser.set(user);
  }

  /**
   * Asks for a new login e-mail. Nothing changes on the account until the link sent to the new
   * address is opened, so the current address stays valid after this resolves.
   */
  async requestEmailChange(req: ChangeEmailRequest): Promise<void> {
    await firstValueFrom(this.http.post(`${environment.apiUrl}/auth/change-email`, req));
  }

  /** Applies a pending e-mail change from the link's token. Callable signed out. */
  async confirmEmailChange(token: string): Promise<void> {
    await firstValueFrom(
      this.http.post(`${environment.apiUrl}/auth/confirm-email-change`, { token }),
    );
  }

  /**
   * Ends the session without navigating away — the confirmation screen has its own outcome to
   * show, and the stored token names the old e-mail as its subject, so it is already dead.
   */
  signOutSilently(): void {
    this.clearSession();
    this.#sessionExpired.set(false);
  }

  /** The e-mail change landed: the session is over and the login screen has to say why. */
  completeEmailChange(): void {
    this.signOutSilently();
    this.#emailChanged.set(true);
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await firstValueFrom(
      this.http.put(`${environment.apiUrl}/auth/change-password`, { currentPassword, newPassword }),
    );
  }

  async setPassword(req: SetPasswordRequest): Promise<void> {
    await firstValueFrom(this.http.post(`${environment.apiUrl}/auth/set-password`, req));
  }

  async requestPasswordReset(email: string): Promise<void> {
    await firstValueFrom(this.http.post(`${environment.apiUrl}/auth/forgot-password`, { email }));
  }

  async acceptInvitation(token: string): Promise<void> {
    await firstValueFrom(this.http.post(`${environment.apiUrl}/auth/accept-invitation`, { token }));
  }

  async login(req: LoginRequest): Promise<void> {
    const response = await firstValueFrom(
      this.http.post<LoginResponse>(`${environment.apiUrl}/auth/login`, req),
    );
    localStorage.setItem(AUTH_TOKEN_KEY, response.token);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(response.user));
    this.#sessionExpired.set(false);
    this.#emailChanged.set(false);
    this.#currentUser.set(response.user);
  }

  /** User-initiated sign out — no explanation needed on the login screen. */
  logout(): void {
    this.clearSession();
    this.#sessionExpired.set(false);
    this.#emailChanged.set(false);
    void this.router.navigate(['/login']);
  }

  logoutDueToExpiredSession(): void {
    if (!this.isAuthenticated()) {
      // Several requests can fail at once — the first one already ended the session.
      return;
    }
    this.clearSession();
    this.#sessionExpired.set(true);
    this.#emailChanged.set(false);
    void this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }

  /**
   * True when there is no token (the stored one by default) or its `exp` falls within
   * {@link EXPIRY_LEEWAY_MS} of now or before it.
   */
  isTokenExpired(token: string | null = this.getToken()): boolean {
    if (!token) {
      return true;
    }
    const expiresAt = this.getTokenExpiry(token);
    // A token without a readable `exp` cannot be judged here — leave it to the server's 401.
    return expiresAt !== null && expiresAt - EXPIRY_LEEWAY_MS <= Date.now();
  }

  private clearSession(): void {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    localStorage.removeItem(REFERRAL_DISMISSED_KEY);
    localStorage.removeItem(TRAINING_DISMISSED_KEY);
    this.#currentUser.set(null);
  }

  /** Token expiry as epoch milliseconds, or `null` when the token carries no usable `exp`. */
  private getTokenExpiry(token: string): number | null {
    const payload = this.decodeJwt(token);
    return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
  }

  private parseStoredUser(userJson: string): AuthUser | null {
    try {
      return JSON.parse(userJson) as AuthUser;
    } catch {
      return null;
    }
  }

  private parseJwt(token: string): AuthUser | null {
    const payload = this.decodeJwt(token);
    if (!payload) {
      return null;
    }
    return {
      email: payload.sub as string,
      firstName: payload.firstName as string,
      lastName: payload.lastName as string,
      role: payload.role as UserRole,
      termsAcceptedVersion: null,
    };
  }

  private decodeJwt(token: string): JwtPayload | null {
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      return JSON.parse(atob(padded)) as JwtPayload;
    } catch {
      return null;
    }
  }
}
